export const theme = {
  // re-defining base colors for use with dots or so, so we can use slightly different shades of base color across the app.
  valid: {
    background: '#00ff0042',
  },
  invalid: {
    background: '#ff000042',
  },
};

export const baseColors = {
  orange: '#ff9d0a',
  yellow: '#ebb15b',
  logo: '#0039a6',
  green: '#1aa053',
  lightGreen: 'lime',
  red: '#eb4c55',
  gray: '#808080',
  white: '#ffffff',
} as const;

const lightColors = {
  globalBackground: '#fbfcfe',
  highlightBackground: '#F3F8FC', // subtle bluish accent surface (e.g. chart bands)
  sidebar: '#ffffff',
  panelBackground: '#ffffff', // '#2E353D',
  divider: '#e4e9fb',
  primaryBorder: '#e4e9fb',
  focus: '#3167FF',
  semitransparent: 'rgba(128, 128, 128, 0.2)',
  approve: 'green',
  reject: 'red',
  warning: '#ff9d0a',
  text: {
    base: '#1a1a1a',
    link: '#2730f5',
    muted: '#6c757d',
  },
  brand: '#3167FF',
  input: {
    background: '#ffffff',
    border: '#ced4da',
    text: '#1a1a1a',
    button: '#181E22',
  },
  buttons: {
    primary: {
      border: '#5389FF',
      background: '#3167FF',
      text: '#1a1a1a',
      hover: '#3167FF',
    },
    secondary: {
      border: '#e4e9fb',
      background: '#e4e9fb',
      text: '#2730f5',
      hover: '#edf1fc',
    },
    ternary: {
      border: '#2730f5',
      background: '#ffffff',
      text: '#2730f5',
      hover: '#3167FF',
    },
    accept: {
      border: '#00A676',
      background: '#00A676',
      text: '#ffffff',
      hover: '#00A676',
    },
    danger: {
      border: '#EB4C55',
      background: '#EB4C55',
      text: '#ffffff',
      hover: '#EB4C55',
    },
    warning: {
      border: '#FFAA00',
      background: '#FFAA00',
      text: '#1a1a1a',
      hover: '#FFAA00',
    },
    gray: {
      background: '#ffffff',
      border: '#f0f0f1',
      text: '#212529',
      hover: '#e4e9fb',
    },
    tag: {
      background: '#6c757d',
      border: '#6c757d',
      text: '#ffffff',
      hover: '#6c757d',
    },
    ghost: {
      background: 'transparent',
      border: '#3167FF',
      text: '#3167FF',
      hover: '#3167FF',
    },
  },
  card: {
    border: '#eeeeee',
    background: '#ffffff',
  },
  success: {
    background: '#00A676',
  },
  danger: {
    background: '#EB4C55',
  },
  toggle: {
    background: '#ffffff',
    accent: '#237bff',
    checkmark: '#FFFFFF',
    border: '#c2c2d5',
  },
  tabs: {
    background: '#f7f7f7',
    border: '#f5f5f5',
    active: '#ffffff',
  },
  badge: {
    background: '#e4e9fb',
    text: '#1a1a1a',
  },
} as const;

const darkColors = {
  globalBackground: '#121212',
  highlightBackground: '#1e2a3a', // subtle bluish accent surface (e.g. chart bands)
  sidebar: '#2e353d',
  panelBackground: '#181e22', // '#2E353D',
  divider: '#3B4247',
  primaryBorder: '#3B4247',
  focus: '#3167FF',
  semitransparent: 'rgba(128, 128, 128, 0.2)',
  approve: 'green',
  reject: 'red',
  warning: '#ff9d0a',
  text: {
    base: '#FFFFFF',
    link: '#5389FF',
    muted: '#9ca3af',
  },
  brand: '#3167FF',
  input: {
    background: '#3B4247',
    border: '#586167',
    text: '#FFFFFF',
    button: '#181E22',
  },
  buttons: {
    primary: {
      border: '#5389FF',
      background: '#3167FF',
      text: '#FFFFFF',
      hover: '#5389FF',
    },
    secondary: {
      border: '#586167',
      background: '#e4e9fb',
      text: '#1a1a1a',
      hover: '#e4e9fb',
    },
    ternary: {
      border: '#5389FF',
      background: 'transparent',
      text: '#5389FF',
      hover: '#5389FF',
    },
    accept: {
      border: '#00A676',
      background: '#00A676',
      text: '#FFFFFF',
      hover: '#00A676',
    },
    danger: {
      border: '#EB4C55',
      background: '#EB4C55',
      text: '#FFFFFF',
      hover: '#EB4C55',
    },
    warning: {
      border: '#FFAA00',
      background: '#FFAA00',
      text: '#FFFFFF',
      hover: '#FFAA00',
    },
    gray: {
      background: '#2E353D',
      border: '#2E353D',
      text: '#FFFFFF',
      hover: '#3B4247',
    },
    tag: {
      background: '#6c757d',
      border: '#6c757d',
      text: '#ffffff',
      hover: '#6c757d',
    },
    ghost: {
      background: 'transparent',
      border: '#3167FF',
      text: '#3167FF',
      hover: '#3167FF',
    },
  },
  card: {
    border: '#586167',
    background: '#181E22',
  },
  success: {
    background: '#00A676',
  },
  danger: {
    background: '#EB4C55',
  },
  toggle: {
    background: '#3B4247',
    accent: '#5389FF',
    checkmark: '#FFFFFF',
    border: '#586167',
  },
  tabs: {
    background: '#3B4247',
    border: '#020202ff',
    active: '#3B4247',
  },
  badge: {
    background: '#AFCCFE',
    text: '#000000',
  },
} as const;

// Detect browser theme preference
export const getBrowserThemePreference = (): 'light' | 'dark' => {
  // FIXME for now, we're not enabling dark mode, but we force light mode with new color pallette
  // so that this code doesnt rot away while design is being worked on.

  // if the url contains ?experimentalDarkMode we allow automatic switching (only checking upon app load).
  // otherwise return light
  if (window?.location?.search?.includes('experimentalDarkMode')) {
    return 'dark'; // default to light mode
  }

  return 'light'; // default to light mode
};

// Set colorScheme based on browser theme preference
// Note: If the user changes their system theme preference, they will need to refresh the page
// to see the updated theme. Implementing dynamic theme updates would require a state-based
// theme system (e.g., React Context or Redux).
export const colorScheme = getBrowserThemePreference() === 'dark' ? darkColors : lightColors;
